import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { MessageCircle, Send, CheckCircle, Clock, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';

const ContactSection = ({ whatsappUrl }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [formData, setFormData] = useState({ name: '', company: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { language } = useLanguage();

  const content = {
    en: {
      overline: 'Get In Touch',
      headline: 'Ready to',
      headlineAccent: 'Scale?',
      subheadline: "Let's discuss how we can transform your marketing.",
      respondsIn: 'Usually responds within 2 hours',
      fastestWay: 'Fastest Way to Reach Us',
      skipWait: 'Message us directly on WhatsApp for an instant consultation.',
      whatsappBtn: 'Message Us on WhatsApp',
      freeConsultation: 'Free Consultation',
      quickResponse: 'Quick Response',
      orLeaveMessage: 'Or Leave a Message',
      getBack: "We'll get back to you within 24 hours.",
      yourName: 'Your Name',
      companyName: 'Company',
      yourMessage: 'Message',
      messagePlaceholder: 'Tell us about your project...',
      sendMessage: 'Send Message',
      messageSent: 'Message Sent!',
      thankYou: "We'll get back to you soon.",
      chatNow: 'Or chat on WhatsApp',
    },
    tr: {
      overline: 'İletişime Geçin',
      headline: 'Büyümeye',
      headlineAccent: 'Hazır mısınız?',
      subheadline: 'Pazarlamanızı nasıl dönüştürebileceğimizi konuşalım.',
      respondsIn: 'Genellikle 2 saat içinde yanıt verir',
      fastestWay: 'Bize Ulaşmanın En Hızlı Yolu',
      skipWait: "Anında danışmanlık için WhatsApp'tan yazın.",
      whatsappBtn: "WhatsApp'tan Mesaj Gönderin",
      freeConsultation: 'Ücretsiz Danışmanlık',
      quickResponse: 'Hızlı Yanıt',
      orLeaveMessage: 'Veya Mesaj Bırakın',
      getBack: '24 saat içinde size döneceğiz.',
      yourName: 'Adınız',
      companyName: 'Şirket',
      yourMessage: 'Mesaj',
      messagePlaceholder: 'Projeniz hakkında bilgi verin...',
      sendMessage: 'Mesaj Gönder',
      messageSent: 'Mesaj Gönderildi!',
      thankYou: 'En kısa sürede döneceğiz.',
      chatNow: "WhatsApp'tan yazın",
    },
  };

  const t = content[language] || content.en;

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', company: '', message: '' });
    }, 4000);
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-[#F9FAFB]" ref={ref}>
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-[#E60000] text-sm uppercase tracking-[0.2em] font-bold">{t.overline}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] mt-3 tracking-tight">
            {t.headline} <span className="text-[#E60000]">{t.headlineAccent}</span>
          </h2>
          <p className="text-[#6B7280] text-base mt-3">{t.subheadline}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* WhatsApp Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-[#0A0A0A] rounded-2xl p-6 md:p-8 text-white"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-xs mb-4">
              <Clock size={12} />
              <span>{t.respondsIn}</span>
            </div>

            <h3 className="font-heading text-xl font-bold mb-2">{t.fastestWay}</h3>
            <p className="text-gray-400 text-sm mb-6">{t.skipWait}</p>

            <motion.a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white w-full py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle size={20} />
              {t.whatsappBtn}
            </motion.a>

            <div className="flex gap-6 mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <Sparkles size={14} className="text-[#E60000]" />
                <span>{t.freeConsultation}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <Clock size={14} className="text-[#E60000]" />
                <span>{t.quickResponse}</span>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm"
          >
            <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1">{t.orLeaveMessage}</h3>
            <p className="text-[#6B7280] text-sm mb-5">{t.getBack}</p>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder={t.yourName}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E60000] focus:ring-1 focus:ring-[#E60000]/20"
                  data-testid="contact-name-input"
                />
                <Input
                  type="text"
                  placeholder={t.companyName}
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E60000] focus:ring-1 focus:ring-[#E60000]/20"
                  data-testid="contact-company-input"
                />
                <Textarea
                  placeholder={t.messagePlaceholder}
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E60000] focus:ring-1 focus:ring-[#E60000]/20 resize-none"
                  data-testid="contact-message-input"
                />
                <motion.button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#E60000] text-white py-3.5 rounded-lg font-bold shadow-md hover:bg-[#CC0000] transition-all duration-300"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  data-testid="contact-form-submit"
                >
                  <Send size={16} />
                  {t.sendMessage}
                </motion.button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <h4 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1">{t.messageSent}</h4>
                <p className="text-[#6B7280] text-sm mb-4">{t.thankYou}</p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#E60000] font-bold text-sm"
                >
                  <MessageCircle size={14} />
                  {t.chatNow}
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
